import storageService from 'src/services/storage-service';
import { CustomerLevelPermissions, ProjectLevelPermissions, UserGroup, UserPermissions } from 'src/shared/types';
import { jwtDecode } from 'jwt-decode';
import config from 'src/config';
import authStorageService from 'src/shared/hooks/services/auth-storage-service';
import apiService, { HTTPMethod } from './api-service';

type TokenExchangeResponse = {
    accessToken: string;
    accessTokenExpiresAt: string;
    refreshToken: string;
    refreshTokenExpiresAt: string;
};

type CustomJwtPayload = {
    token: string;
    isInternalUser: boolean;
    tncConsent: boolean;
    permissions: {
        customerLevelPermissions: CustomerLevelPermissions[];
        projectLevelPermissions: ProjectLevelPermissions[];
    };
    cognitoId: string;
    firstName: string;
    lastName: string;
    userEmail: string;
    groups: UserGroup[];
    isAdmin: boolean;
    userEnabled: boolean;
};

export class AuthService {
    public isRefreshing = false;
    private refreshSessionTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        const accessToken = authStorageService.getAccessToken();
        const accessTokenExpiry = authStorageService.getAccessTokenExpiry();

        if (accessToken && accessTokenExpiry && new Date(accessTokenExpiry) > new Date()) {
            this.scheduleAutoRefresh();
            return;
        }

        const refreshToken = authStorageService.getRefreshToken();
        const refreshTokenExpiry = authStorageService.getRefreshTokenExpiry();

        if (refreshToken && refreshTokenExpiry && new Date(refreshTokenExpiry) > new Date()) {
            this.refreshSession();
        }
    }

    public async refreshSession() {
        const refreshToken = authStorageService.getRefreshToken();
        const refreshTokenExpiry = authStorageService.getRefreshTokenExpiry();

        if (refreshToken && refreshTokenExpiry && new Date(refreshTokenExpiry) > new Date()) {
            this.isRefreshing = true;
            try {
                const newAuthData = await apiService.fetch(`${config.apiBaseUrl}/renew`, {
                    method: HTTPMethod.POST,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        token: refreshToken,
                    }),
                    noCache: true, // Don't cache auth requests
                });

                await this.storeAuthData(newAuthData);
            } catch (error) {
                // Don't log auth errors as errors - they're expected user flow
                console.info('Token refresh failed - user will be signed out:', (error as Error).message);
                this.signout();
                // Don't re-throw auth errors to prevent them from going to Sentry
            } finally {
                this.isRefreshing = false;
            }
        } else {
            console.info('Refresh token expired or missing, signing out user');
            this.signout();
        }
    }

    /**
     * Signout user
     */
    public async signout(callback?: () => void) {
        const accessToken = authStorageService.getAccessToken();
        storageService.clearAll();
        try {
            await apiService.fetch(`${config.apiBaseUrl}/logout`, {
                method: HTTPMethod.POST,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: accessToken as string,
                },
                ignoreErrors: true, // Don't throw errors for logout failures
            });
        } finally {
            callback?.();
            if (window.location.pathname !== '/') {
                const { cognitoLoginUrl, cognitoClientId } = config;
                const locationOrigin = window.location.origin;
                const cognitoRedirectUrl = `${locationOrigin}/oauth?provider=aws`;
                const staticParams = `client_id=${cognitoClientId}&response_type=code&scope=aws.cognito.signin.user.admin`;
                const cognitoLogoutUrl = `${cognitoLoginUrl}logout?${staticParams}&redirect_uri=${encodeURIComponent(cognitoRedirectUrl)}`;
                window.location.replace(cognitoLogoutUrl);
            }
        }
    }

    private scheduleAutoRefresh() {
        const accessToken = authStorageService.getAccessToken();
        const accessTokenExpiry = authStorageService.getAccessTokenExpiry();

        if (!accessToken || !accessTokenExpiry) {
            return;
        }

        // Cancel any existing refresh session timeout
        if (this.refreshSessionTimeout) {
            clearTimeout(this.refreshSessionTimeout);
        }

        // Timeout to refresh the session 14 minutes before it expires
        const expiresAt = new Date(accessTokenExpiry).getTime();

        let autoRefreshDelayInMilliseconds = expiresAt - Date.now() - 5 * 60 * 1000;

        if (autoRefreshDelayInMilliseconds < 0) {
            autoRefreshDelayInMilliseconds = 0;
        }

        this.refreshSessionTimeout = setTimeout(() => {
            this.refreshSession();
        }, autoRefreshDelayInMilliseconds);
    }

    /**
     * Store token data in local storage
     * @param authData - Token exchange response data
     */
    private storeAuthData(authData: TokenExchangeResponse) {
        authStorageService.setAccessToken(authData.accessToken);
        authStorageService.setRefreshToken(authData.refreshToken);
        authStorageService.setAccessTokenExpiry(authData.accessTokenExpiresAt);
        authStorageService.setRefreshTokenExpiry(authData.refreshTokenExpiresAt);

        this.scheduleAutoRefresh();

        // Dispatch an event on window saying auth data has refreshed
        const event = new CustomEvent('auth_data_refreshed', {
            detail: {
                accessToken: authData.accessToken,
                accessTokenExpiry: authData.accessTokenExpiresAt,
            },
            bubbles: true,
        });

        window.dispatchEvent(event);
    }

    /**
     * Exchange token for user permissions
     * @returns User permissions and token data
     */
    public async fetchUserPermissions(code?: string) {
        const userPermissions = this.decodePermissionsFromAccessToken();

        if (userPermissions) {
            return userPermissions;
        }

        if (!code) {
            authStorageService.clearAll();
            this.signout();
            // Create a user-friendly auth error that won't be sent to Sentry
            const authError = new Error('Authentication required - please log in again');
            authError.name = 'AuthenticationError';
            throw authError;
        }
        try {
            const authData = await apiService.fetch(`${config.apiBaseUrl}/login`, {
                method: HTTPMethod.POST,
                body: JSON.stringify({ code, provider: 'aws' }),
            });

            this.storeAuthData(authData);
            return this.decodePermissionsFromAccessToken() as UserPermissions;
        } catch (error) {
            console.info('Token exchange failed:', (error as Error).message);
            // Create a user-friendly auth error that won't be sent to Sentry
            const authError = new Error('Login failed - please try again');
            authError.name = 'AuthenticationError';
            throw authError;
        }
    }

    /**
     * Get user permissions from token
     * @returns User permissions
     */
    public getUserPermissions() {
        return this.decodePermissionsFromAccessToken();
    }

    /**
     * Decode token and return user permissions and token data
     * @returns Decoded token data
     */
    private decodePermissionsFromAccessToken() {
        const accessToken = authStorageService.getAccessToken();

        if (!accessToken) {
            return null;
        }

        try {
            const decodedToken = jwtDecode<CustomJwtPayload>(accessToken);
            return {
                customerLevelPermissions: decodedToken.permissions.customerLevelPermissions,
                projectLevelPermissions: decodedToken.permissions.projectLevelPermissions,
                tnc_consent: decodedToken.tncConsent,
                is_internal_user: decodedToken.isInternalUser,
                token: accessToken,
            };
        } catch (error) {
            console.info('Invalid token detected, clearing auth data');
            authStorageService.clearAll();
            return null;
        }
    }

    /**
     * Get current user data from access token
     * @returns User data or null if no access token
     */
    public getCurrentUserData() {
        const accessToken = authStorageService.getAccessToken();
        const refreshToken = authStorageService.getRefreshToken();
        const accessTokenExpiry = authStorageService.getAccessTokenExpiry();
        const refreshTokenExpiry = authStorageService.getRefreshTokenExpiry();

        if (!accessToken) {
            return null;
        }

        try {
            const decodedToken = jwtDecode<CustomJwtPayload>(accessToken);
            return {
                cognitoId: decodedToken.cognitoId,
                accessToken: accessToken,
                refreshToken: refreshToken || '',
                jwtToken: accessToken,
                email: decodedToken.userEmail,
                isAdmin: decodedToken.isAdmin,
                groups: decodedToken.groups,
                lastName: decodedToken.lastName,
                firstName: decodedToken.firstName,
                accessTokenExpiry: accessTokenExpiry,
                refreshTokenExpiry: refreshTokenExpiry,
            };
        } catch (error) {
            console.info('Invalid token detected in getCurrentUserData, clearing auth data');
            authStorageService.clearAll();
            return null;
        }
    }

    /**
     * Get access token with automatic refresh and retry logic
     * @returns Access token or null if no token
     */
    public async getAccessToken(): Promise<string | null> {
        const accessToken = authStorageService.getAccessToken();
        const accessTokenExpiry = authStorageService.getAccessTokenExpiry();

        // Check if token is expired or missing
        if (!accessToken || !accessTokenExpiry || new Date(accessTokenExpiry) < new Date()) {
            if (this.isRefreshing) {
                // Wait for the refresh to complete with timeout
                let count = 0;
                const maxWaitTime = 10; // 10 seconds max wait
                while (this.isRefreshing && count < maxWaitTime) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    count++;
                }

                // If still refreshing after timeout, sign out
                if (this.isRefreshing) {
                    console.info('Token refresh timeout, signing out user');
                    this.signout();
                    return null;
                } else {
                    return authStorageService.getAccessToken();
                }
            }

            try {
                await this.refreshSession();
                return authStorageService.getAccessToken();
            } catch (error) {
                console.info('Failed to refresh token, user will be signed out');
                return null;
            }
        }

        return accessToken;
    }
}
const authService = new AuthService();
export default authService;
